/**
 * Rate Limiter Utility - Client-side rate limiting for security
 * 
 * Prevents abuse of API calls and protects against DoS attacks
 * Uses localStorage to persist limits across page reloads
 * 
 * Usage:
 * const limiter = new RateLimiter('createNote', 10, 60000); // 10 calls per minute
 * if (limiter.canProceed()) {
 *   // Perform action
 *   limiter.recordCall();
 * } else {
 *   // Show error: rate limit exceeded
 * }
 */

class RateLimiter {
  /**
   * @param {string} key - Unique identifier for this rate limiter
   * @param {number} maxCalls - Maximum number of calls allowed
   * @param {number} windowMs - Time window in milliseconds
   */
  constructor(key, maxCalls, windowMs) {
    this.key = `rateLimiter_${key}`;
    this.maxCalls = maxCalls;
    this.windowMs = windowMs;
  }

  /**
   * Get the call history from localStorage
   * @returns {Array<number>} Array of timestamps
   */
  getCallHistory() {
    try {
      const stored = localStorage.getItem(this.key);
      if (!stored) return [];
      
      const history = JSON.parse(stored);
      // Filter out old calls outside the time window
      const now = Date.now();
      return history.filter(timestamp => now - timestamp < this.windowMs);
    } catch (error) {
      console.error('Error reading rate limiter history:', error);
      return [];
    }
  }

  /**
   * Save call history to localStorage
   * @param {Array<number>} history - Array of timestamps
   */
  saveCallHistory(history) {
    try {
      localStorage.setItem(this.key, JSON.stringify(history));
    } catch (error) {
      console.error('Error saving rate limiter history:', error);
    }
  }

  /**
   * Check if the user can proceed with the action
   * @returns {boolean} True if under the limit, false otherwise
   */
  canProceed() {
    const history = this.getCallHistory();
    return history.length < this.maxCalls;
  }

  /**
   * Record a new call
   */
  recordCall() {
    const history = this.getCallHistory();
    history.push(Date.now());
    this.saveCallHistory(history);
  }

  /**
   * Get the number of calls made in the current window
   * @returns {number}
   */
  getCallCount() {
    return this.getCallHistory().length;
  }

  /**
   * Get the remaining number of calls allowed
   * @returns {number}
   */
  getRemainingCalls() {
    return Math.max(0, this.maxCalls - this.getCallCount());
  }

  /**
   * Get the time until the limit resets (in milliseconds)
   * @returns {number}
   */
  getResetTime() {
    const history = this.getCallHistory();
    if (history.length === 0) return 0;
    
    const oldestCall = Math.min(...history);
    const resetTime = oldestCall + this.windowMs;
    return Math.max(0, resetTime - Date.now());
  }

  /**
   * Reset the rate limiter (clear all history)
   */
  reset() {
    try {
      localStorage.removeItem(this.key);
    } catch (error) {
      console.error('Error resetting rate limiter:', error);
    }
  }

  /**
   * Get a human-readable message about the rate limit status
   * @returns {string}
   */
  getStatusMessage() {
    const remaining = this.getRemainingCalls();
    const resetTimeMs = this.getResetTime();
    const resetTimeMinutes = Math.ceil(resetTimeMs / 60000);
    
    if (remaining > 0) {
      return `${remaining} of ${this.maxCalls} actions remaining`;
    } else {
      return `Rate limit exceeded. Please wait ${resetTimeMinutes} minute${resetTimeMinutes !== 1 ? 's' : ''}`;
    }
  }
}

/**
 * Throttle function - Ensures a function is not called more than once in a specified time period
 * @param {Function} func - The function to throttle
 * @param {number} delay - Delay in milliseconds
 * @returns {Function} Throttled function
 */
export function throttle(func, delay) {
  let lastCall = 0;
  let timeoutId = null;

  return function throttled(...args) {
    const now = Date.now();
    const timeSinceLastCall = now - lastCall;

    if (timeSinceLastCall >= delay) {
      lastCall = now;
      return func.apply(this, args);
    } else {
      // Clear existing timeout
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      // Schedule the function to run after the remaining delay
      return new Promise((resolve) => {
        timeoutId = setTimeout(() => {
          lastCall = Date.now();
          resolve(func.apply(this, args));
        }, delay - timeSinceLastCall);
      });
    }
  };
}

/**
 * Debounce function - Delays execution until after a specified time has elapsed since the last call
 * @param {Function} func - The function to debounce
 * @param {number} delay - Delay in milliseconds
 * @returns {Function} Debounced function
 */
export function debounce(func, delay) {
  let timeoutId = null;

  return function debounced(...args) {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    return new Promise((resolve) => {
      timeoutId = setTimeout(() => {
        resolve(func.apply(this, args));
      }, delay);
    });
  };
}

export default RateLimiter;

